<script setup>
console.log('🚀 GM Analytics Dashboard Component: Initializing...');

import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import GmMetricCard from './metrics/GmMetricCard.vue';
import GmKPICard from './metrics/GmKPICard.vue';
import GmTrendCard from './metrics/GmTrendCard.vue';
import GmAIMessagesCard from './metrics/GmAIMessagesCard.vue';
import GmConversationTrendChart from './charts/GmConversationTrendChart.vue';
import GmSentimentTrendChart from './charts/GmSentimentTrendChart.vue';
import GmResponseTimeChart from './charts/GmResponseTimeChart.vue';
import GmChannelPerformanceChart from './charts/GmChannelPerformanceChart.vue';

console.log('📊 GM Analytics Dashboard Component: Components imported successfully');

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

const { t } = useI18n();
const store = useStore();

// Reactive data
const dashboardMetrics = ref({});
const kpiData = ref({});
const trendData = ref({});
const channels = ref([]);
const agents = ref([]);

console.log('🔄 GM Analytics Dashboard Component: Props received:', props.data);

// Computed properties
const hasValidData = computed(() => {
  const hasData = Object.keys(props.data).length > 0;
  console.log('📊 GM Analytics Dashboard Component: Has valid data:', hasData);
  return hasData;
});

const totalConversations = computed(() => {
  const total = props.data?.totalConversations || 0;
  console.log('📊 GM Analytics Dashboard Component: Total conversations:', total);
  return total;
});

const activeConversations = computed(() => {
  const active = props.data?.activeConversations || 0;
  console.log('📊 GM Analytics Dashboard Component: Active conversations:', active);
  return active;
});

const totalMessages = computed(() => {
  const total = props.data?.totalMessages || 0;
  console.log('📊 GM Analytics Dashboard Component: Total messages:', total);
  return total;
});


const aiMessages = computed(() => {
  const ai = props.data?.aiMessages || 0;
  console.log('📊 GM Analytics Dashboard Component: AI messages:', ai);
  return ai;
});

const captainMessages = computed(() => {
  const captain = props.data?.captainMessages || 0;
  console.log('📊 GM Analytics Dashboard Component: Captain messages:', captain);
  return captain;
});

const agentBotMessages = computed(() => {
  const agentBot = props.data?.agentBotMessages || 0;
  console.log('📊 GM Analytics Dashboard Component: AgentBot messages:', agentBot);
  return agentBot;
});

const sentimentScore = computed(() => {
  const score = props.data?.sentimentScore || 0;
  console.log('📊 GM Analytics Dashboard Component: Sentiment score:', score);
  return score;
});

const fcrRate = computed(() => {
  const rate = props.data?.fcrRate || 0;
  console.log('📊 GM Analytics Dashboard Component: FCR rate:', rate);
  return rate;
});

const avgResponseTime = computed(() => {
  const time = props.data?.avgResponseTime || 0;
  console.log('📊 GM Analytics Dashboard Component: Avg response time:', time);
  return time;
});

const resolutionTime = computed(() => {
  const time = props.data?.resolutionTime || 0;
  console.log('📊 GM Analytics Dashboard Component: Resolution time:', time);
  return time;
});

const currentFilters = computed(() => {
  const filters = store.getters['gmAnalytics/filters'];
  console.log('🔧 GM Analytics Dashboard: currentFilters computed:', filters);
  if (filters) {
    console.log('🔧 GM Analytics Dashboard: currentFilters dateRange:', filters.dateRange);
    console.log('🔧 GM Analytics Dashboard: currentFilters preset:', filters.dateRange?.preset);
  }
  return filters || {};
});

// Methods
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const formatTime = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  } else {
    return `${Math.floor(seconds / 3600)}h`;
  }
};

const getSentimentColor = (score) => {
  if (score >= 0.7) return '#10b981'; // Green
  if (score >= 0.4) return '#f59e0b'; // Yellow
  return '#ef4444'; // Red
};

const getSentimentLabel = (score) => {
  if (score >= 0.7) return 'Positive';
  if (score >= 0.4) return 'Neutral';
  return 'Negative';
};

const conversationTrend = computed(() => {
  const trend = props.data?.conversationTrend || [];
  console.log('📊 GM Analytics Dashboard Component: Conversation trend:', trend);
  return trend;
});

const sentimentTrend = computed(() => {
  const trend = props.data?.sentimentTrend || [];
  console.log('📊 GM Analytics Dashboard Component: Sentiment trend:', trend);
  return trend;
});

const responseTimeTrend = computed(() => {
  const trend = props.data?.responseTimeTrend || [];
  console.log('📊 GM Analytics Dashboard Component: Response time trend:', trend);
  return trend;
});

const channelPerformance = computed(() => {
  const performance = props.data?.channelPerformance || [];
  console.log('📊 GM Analytics Dashboard Component: Channel performance:', performance);
  return performance;
});

// Lifecycle
// Methods
const handleChartRefresh = (chartType, filters) => {
  console.log(`🔄 GM Analytics Dashboard: ${chartType} chart refresh requested with filters:`, filters);
  // Emit refresh event to parent component
  emit('chart-refresh', { chartType, filters });
};

const handleChartExport = (chartType, filters) => {
  console.log(`📊 GM Analytics Dashboard: ${chartType} chart export requested with filters:`, filters);
  // Emit export event to parent component
  emit('chart-export', { chartType, filters });
};

const handleFiltersChange = (chartType, filters) => {
  console.log(`🔧 GM Analytics Dashboard: ${chartType} chart filters changed:`, filters);
  console.log('🔧 GM Analytics Dashboard: Filters type:', typeof filters);
  console.log('🔧 GM Analytics Dashboard: Filters keys:', Object.keys(filters));
  console.log('🔧 GM Analytics Dashboard: Date range in filters:', filters.dateRange);
  console.log('🔧 GM Analytics Dashboard: Preset value:', filters.dateRange?.preset);
  
  // Emit filters change event to parent component
  emit('filters-change', { chartType, filters });
};

// Emits
const emit = defineEmits(['chart-refresh', 'chart-export', 'filters-change']);

onMounted(async () => {
  console.log('🎯 GM Analytics Dashboard Component: Component mounted');
  console.log('📊 GM Analytics Dashboard Component: Dashboard data:', props.data);
  
  // Load channels and agents data from store/API
  try {
    console.log('🔄 GM Analytics Dashboard: Loading channels and agents...');
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('agents/get')
    ]);
    
    // Get channels and agents from store
    channels.value = store.getters['inboxes/getInboxes'] || [];
    agents.value = store.getters['agents/getAgents'] || [];
    
    console.log('✅ GM Analytics Dashboard: Channels loaded:', channels.value.length);
    console.log('✅ GM Analytics Dashboard: Agents loaded:', agents.value.length);
  } catch (error) {
    console.error('❌ GM Analytics Dashboard: Error loading channels/agents:', error);
    channels.value = [];
    agents.value = [];
  }
});

console.log('✅ GM Analytics Dashboard Component: Component setup completed');
</script>

<template>
  <div class="gm-analytics-dashboard">
    <div v-if="!hasValidData" class="gm-no-data">
      <div class="gm-no-data-content">
        <div class="gm-no-data-icon">📊</div>
        <h3>{{ t('GM_ANALYTICS.DASHBOARD.NO_DATA_TITLE', 'No Data Available') }}</h3>
        <p>{{ t('GM_ANALYTICS.DASHBOARD.NO_DATA_SUBTITLE', 'Analytics data will appear here once available') }}</p>
      </div>
    </div>

    <div v-else class="gm-dashboard-content">
      <!-- Key Performance Indicators -->
      <div class="gm-kpi-section">
        <div class="gm-kpi-grid">
          <GmKPICard
            :title="t('GM_ANALYTICS.DASHBOARD.TOTAL_CONVERSATIONS', 'Total Conversations')"
            :value="formatNumber(totalConversations)"
            :description="t('GM_ANALYTICS.DASHBOARD.TOTAL_CONVERSATIONS_DESC', 'Last 30 days')"
            icon="💬"
            color="#3b82f6"
          />
          
          <GmKPICard
            :title="t('GM_ANALYTICS.DASHBOARD.ACTIVE_CONVERSATIONS', 'Active Conversations')"
            :value="formatNumber(activeConversations)"
            :description="t('GM_ANALYTICS.DASHBOARD.ACTIVE_CONVERSATIONS_DESC', 'Currently open conversations')"
            icon="🟢"
            color="#10b981"
          />
          
          <GmKPICard
            :title="t('GM_ANALYTICS.DASHBOARD.TOTAL_MESSAGES', 'Total Messages')"
            :value="formatNumber(totalMessages)"
            :description="t('GM_ANALYTICS.DASHBOARD.TOTAL_MESSAGES_DESC', 'Last 30 days')"
            icon="📨"
            color="#8b5cf6"
          />
          
                  <GmAIMessagesCard
                    :title="t('GM_ANALYTICS.DASHBOARD.AI_MESSAGES', 'AI Messages')"
                    :total-ai-messages="aiMessages"
                    :captain-messages="captainMessages"
                    :agent-bot-messages="agentBotMessages"
                    :description="t('GM_ANALYTICS.DASHBOARD.AI_MESSAGES_DESC', 'AI responses sent till date')"
                    color="#f59e0b"
                  />
        </div>
      </div>

      <!-- Performance Metrics -->
      <div class="gm-metrics-section">
        <div class="gm-metrics-grid">
          <GmMetricCard
            :title="t('GM_ANALYTICS.DASHBOARD.SENTIMENT_SCORE', 'Sentiment Score')"
            :value="(sentimentScore * 100).toFixed(1) + '%'"
            :description="getSentimentLabel(sentimentScore)"
            :color="getSentimentColor(sentimentScore)"
            icon="😊"
          />
          
          <GmMetricCard
            :title="t('GM_ANALYTICS.DASHBOARD.FCR_RATE', 'First Contact Resolution')"
            :value="(fcrRate * 100).toFixed(1) + '%'"
            :description="t('GM_ANALYTICS.DASHBOARD.FCR_RATE_DESC', 'Issues resolved in first contact')"
            color="#10b981"
            icon="✅"
          />
          
          <GmMetricCard
            :title="t('GM_ANALYTICS.DASHBOARD.AVG_RESPONSE_TIME', 'Avg Response Time')"
            :value="formatTime(avgResponseTime)"
            :description="t('GM_ANALYTICS.DASHBOARD.AVG_RESPONSE_TIME_DESC', 'Average time to first response')"
            color="#3b82f6"
            icon="⏱️"
          />
          
          <GmMetricCard
            :title="t('GM_ANALYTICS.DASHBOARD.RESOLUTION_TIME', 'Resolution Time')"
            :value="formatTime(resolutionTime)"
            :description="t('GM_ANALYTICS.DASHBOARD.RESOLUTION_TIME_DESC', 'Average time to resolve')"
            color="#8b5cf6"
            icon="🎯"
          />
        </div>
      </div>

      <!-- Charts Section -->
      <div class="gm-charts-section">
        <div class="gm-charts-grid">
          <GmConversationTrendChart
            :data="conversationTrend"
            :title="t('GM_ANALYTICS.DASHBOARD.CONVERSATION_TREND', 'Conversation Volume Trend')"
            :height="300"
            :channels="channels"
            :agents="agents"
            :filters="currentFilters"
            @refresh="(filters) => handleChartRefresh('conversation-trend', filters)"
            @export="(filters) => handleChartExport('conversation-trend', filters)"
            @filters-change="(filters) => handleFiltersChange('conversation-trend', filters)"
          />
          <GmSentimentTrendChart
            :data="sentimentTrend"
            :title="t('GM_ANALYTICS.DASHBOARD.SENTIMENT_TREND', 'Sentiment Trend')"
            :height="300"
            :channels="channels"
            :agents="agents"
            :filters="currentFilters"
            @refresh="(filters) => handleChartRefresh('sentiment-trend', filters)"
            @export="(filters) => handleChartExport('sentiment-trend', filters)"
            @filters-change="(filters) => handleFiltersChange('sentiment-trend', filters)"
          />
          <GmResponseTimeChart
            :data="responseTimeTrend"
            :title="t('GM_ANALYTICS.DASHBOARD.RESPONSE_TIME_TREND', 'Response Time Trend')"
            :height="300"
            :channels="channels"
            :agents="agents"
            :filters="currentFilters"
            @refresh="(filters) => handleChartRefresh('response-time', filters)"
            @export="(filters) => handleChartExport('response-time', filters)"
            @filters-change="(filters) => handleFiltersChange('response-time', filters)"
          />
          <GmChannelPerformanceChart
            :data="channelPerformance"
            :title="t('GM_ANALYTICS.DASHBOARD.CHANNEL_PERFORMANCE', 'Channel Performance')"
            :height="300"
            :channels="channels"
            :agents="agents"
            :filters="currentFilters"
            @refresh="(filters) => handleChartRefresh('channel-performance', filters)"
            @export="(filters) => handleChartExport('channel-performance', filters)"
            @filters-change="(filters) => handleFiltersChange('channel-performance', filters)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-analytics-dashboard {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

.gm-no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.gm-no-data-content {
  text-align: center;
  color: #64748b;
  max-width: 400px;
}

.gm-no-data-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.gm-no-data-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.5rem 0;
}

.gm-no-data-content p {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.gm-dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.gm-kpi-section,
.gm-metrics-section {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
}

.gm-kpi-grid,
.gm-metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

.gm-charts-section {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  margin-top: 1.5rem;
}

.gm-charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

@media (max-width: 1200px) {
  .gm-kpi-grid,
  .gm-metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .gm-kpi-grid,
  .gm-metrics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .gm-kpi-section,
  .gm-metrics-section {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .gm-analytics-dashboard {
    padding: 0;
  }
  
  .gm-dashboard-content {
    gap: 1.5rem;
  }
}
</style>
